import { Injectable } from '@angular/core';
import Swal, { SweetAlertIcon } from 'sweetalert2';

@Injectable({ providedIn: 'root' })
export class SwalService {
  // ── TOAST (top-end, sparisce da solo) ─────────────────────────────────────
  toast(icon: SweetAlertIcon, title: string, timer = 2500) {
    return Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer,
      timerProgressBar: true,
      background: '#fdf8f3',
      color: 'var(--text-dark)',
      iconColor:
        icon === 'success'
          ? 'var(--gold)'
          : icon === 'error'
            ? 'var(--rose)'
            : 'var(--text-dark)',
      customClass: {
        popup: 'swal-toast-custom',
      },
    }).fire({ icon, title });
  }

  success(title: string, timer = 2500) {
    return this.toast('success', title, timer);
  }

  error(title: string, timer = 3000) {
    return this.toast('error', title, timer);
  }

  // ── DIALOG (modale centrata con bottone) ──────────────────────────────────
  dialog(icon: SweetAlertIcon, title: string, text?: string) {
    return Swal.fire({
      icon,
      title,
      text,
      background: '#fdf8f3',
      color: 'var(--text-dark)',
      confirmButtonColor: 'var(--gold)',
      confirmButtonText: 'Ok',
      customClass: {
        popup: 'swal-dialog-custom',
        confirmButton: 'swal-btn-custom',
      },
    });
  }

  warning(title: string, text?: string) {
    return this.dialog('warning', title, text);
  }

  errorDialog(title: string, text?: string) {
    return this.dialog('error', title, text);
  }

  // ── CONFIRM (chiede conferma prima di procedere) ───────────────────────────
  confirm(
    title: string,
    text?: string,
    confirmText = 'Conferma',
    cancelText = 'Annulla',
  ) {
    return Swal.fire({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      background: '#fdf8f3',
      color: 'var(--text-dark)',
      confirmButtonColor: 'var(--rose)',
      cancelButtonColor: 'var(--text-light)',
      customClass: {
        popup: 'swal-dialog-custom',
        confirmButton: 'swal-btn-custom',
      },
      reverseButtons: true,
    });
  }

  // ── PROMPT con textarea (es. richiesta attivazione piano) ─────────────────
  requestPrompt(
    title: string,
    text?: string,
    confirmText = 'Invia richiesta',
    placeholder = 'Perché ti interessa? (facoltativo)',
  ) {
    return Swal.fire({
      title,
      text,
      input: 'textarea',
      inputPlaceholder: placeholder,
      inputAttributes: { 'aria-label': placeholder },
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: 'Annulla',
      background: '#fdf8f3',
      color: 'var(--text-dark)',
      confirmButtonColor: 'var(--gold)',
      cancelButtonColor: 'var(--text-light)',
      customClass: {
        popup: 'swal-dialog-custom',
        confirmButton: 'swal-btn-custom',
      },
      reverseButtons: true,
    });
  }

  // ── CONFIRM con contenuto HTML (brandizzato) ──────────────────────────────
  confirmHtml(
    title: string,
    html: string,
    confirmText = 'Conferma',
    cancelText = 'Annulla',
    icon: SweetAlertIcon | null = 'question',
  ) {
    return Swal.fire({
      title,
      html,
      icon: icon ?? undefined,
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      background: '#fdf8f3',
      color: 'var(--text-dark)',
      confirmButtonColor: 'var(--gold)',
      cancelButtonColor: 'var(--text-light)',
      customClass: {
        popup: 'swal-dialog-custom',
        confirmButton: 'swal-btn-custom',
      },
      reverseButtons: true,
    });
  }

  // ── SELECT (scelta a radio, brandizzato) ──────────────────────────────────
  select(
    title: string,
    options: Record<string, string>,
    confirmText = 'Conferma',
    text?: string,
  ) {
    return Swal.fire<string>({
      title,
      html: text
        ? `<p style="margin:.5rem 0 .25rem">${text}</p>`
        : undefined,
      input: 'radio',
      inputOptions: options,
      inputValue: Object.keys(options)[0],
      background: '#fdf8f3',
      color: 'var(--text-dark)',
      confirmButtonColor: 'var(--gold)',
      confirmButtonText: confirmText,
      showCancelButton: true,
      cancelButtonText: 'Annulla',
      customClass: {
        popup: 'swal-dialog-custom',
        confirmButton: 'swal-btn-custom',
      },
    });
  }

  // ── LIST WARNING (avviso con elenco, es. campi mancanti) ───────────────────
  listWarning(title: string, items: string[], intro?: string) {
    const list = items.map((i) => `<li>${i}</li>`).join('');
    const html = `${intro ? `<p>${intro}</p>` : ''}<ul style="text-align:left;margin:.5rem auto 0;display:inline-block;padding-left:1.2rem">${list}</ul>`;
    return Swal.fire({
      icon: 'warning',
      title,
      html,
      background: '#fdf8f3',
      color: 'var(--text-dark)',
      confirmButtonColor: 'var(--gold)',
      confirmButtonText: 'Ok',
      customClass: {
        popup: 'swal-dialog-custom',
        confirmButton: 'swal-btn-custom',
      },
    });
  }

  // ── LOADING ───────────────────────────────────────────────────────────────
  loading(title = '') {
    Swal.fire({
      html: `<div class="nyzed-loader"></div>${
        title ? `<p class="nyzed-loader-text">${title}</p>` : ''
      }`,
      allowOutsideClick: false,
      showConfirmButton: false,
      background: 'transparent',
      backdrop: 'rgba(253, 248, 243, 0.65)',
      customClass: { popup: 'swal-loading-custom' },
    });
  }

  isOpen(): boolean {
    return Swal.isVisible();
  }

  close() {
    Swal.close();
  }
}
